import * as React from 'react'
import Table from '../../../common/src/components/Table'
import { TasksTableMultiselect } from '../../../common/src/components/TasksTableMultiselect'
import Toggle from '../../../common/src/components/Toggle'
import Tooltip from '../../../common/src/components/Tooltip'
import { InputLarge, BodyTextSpan } from '../../../common/src/components/core'
import styled from '../../../common/src/components/styled'
import { Colors, Fonts, ColorsRGB, LightColors, DarkColors } from '../../../common/src/constants'
import { appContext } from '../../../common/src/context'
import { fetchFirmTasksQuery } from '../../../common/src/data/queries'
import { FetchFirmTasksQuery, FetchFirmTasksQueryVariables } from '../../../common/src/types.gen'
import validate from '../../../common/src/validate'
import { createRequestScopedClient } from '../apolloClientForLambda'
import { getDataFromUrl } from '../location'
import { createWidget } from './base'

type Task = FetchFirmTasksQuery['firm']['tasks'][0]

interface Props {
  firm: FetchFirmTasksQuery['firm']
}

const addAgentWidgetId = 'add-agent'
const addAgentWidget = createWidget(addAgentWidgetId, (props: Props) => <AddAgent {...props} />, Colors.ink)

class AddAgent extends React.PureComponent<
  Props,
  {
    precedence: string
    saveSuccess: boolean
    errorTaskIds: string
    errorFirstName: string
    errorLastName: string
    errorEmail: JSX.Element | string
    errorWorkHours: string
    selectedTaskIds: Set<React.ReactText>
    errorMessage: string
    submitting: boolean
  }
> {
  private firstNameRef = React.createRef<HTMLInputElement>()
  private lastNameRef = React.createRef<HTMLInputElement>()
  private addressEmailRef = React.createRef<HTMLInputElement>()
  private workHoursRef = React.createRef<HTMLInputElement>()

  constructor(props: Props) {
    super(props)

    this.state = {
      precedence: '1',
      saveSuccess: false,
      errorTaskIds: '',
      errorFirstName: '',
      errorLastName: '',
      errorEmail: '',
      errorWorkHours: '',
      selectedTaskIds: new Set(),
      errorMessage: '',
      submitting: false,
    }
  }

  private successBody = () => {
    const name = `${this.firstNameRef.current!.value.trim()} ${this.lastNameRef.current!.value.trim()}`
    const email = this.addressEmailRef.current!.value.trim()
    return (
      <FormWrapper>
        <Body>
          <Spacer height={64} />
          <H1>Thank you</H1>
          <IntroText>{`I added ${name} <${email}> to these properties:`}</IntroText>
          <Spacer height={16} />
          {this.propertiesTable()}
          <Spacer height={64} />
        </Body>
      </FormWrapper>
    )
  }

  private roles = [
    { key: '1', name: 'Leasing agent' },
    { key: '2', name: 'Assistant manager' },
    { key: '3', name: 'Property manager' },
  ]

  private onSelectedTaskIdsChange = (selectedTaskIds: Set<React.ReactText>) => this.setState({ selectedTaskIds })

  private toggleOnClick = (vals: string[]) => this.setState({ precedence: vals[0] })

  private addAgentButtonOnClick = () => {
    const { precedence, selectedTaskIds } = this.state
    const { firm } = this.props
    const { apiKey } = getDataFromUrl<{ apiKey: string }>()

    this.setState({ submitting: true })

    let errorTaskIds = ''
    let errorFirstName = ''
    let errorLastName = ''
    let errorWorkHours = ''
    let errorEmail = ''

    if (selectedTaskIds.size === 0) {
      errorTaskIds = 'must select a property'
    }

    const firstName = this.firstNameRef.current!.value.trim()
    if (!firstName) {
      errorFirstName = 'first name cannot be blank'
    }

    const lastName = this.lastNameRef.current!.value.trim()
    if (!lastName) {
      errorLastName = 'last name cannot be blank'
    }
    const addressEmail = this.addressEmailRef.current!.value.trim()
    const validatedAddressEmail = validate.email().validate(addressEmail)

    if (!validatedAddressEmail.value) {
      errorEmail = 'email must be valid'
    }

    const workHours = this.workHoursRef.current!.value.trim()
    if (!this.workHoursValid(workHours)) {
      errorWorkHours = 'must have showing hours'
    }

    this.setState({ errorTaskIds, errorFirstName, errorLastName, errorEmail, errorWorkHours })

    if (errorTaskIds || errorFirstName || errorLastName || errorEmail || errorWorkHours) {
      this.setState({ submitting: false })
      return
    }

    fetch('/api/post_add_agent', {
      method: 'POST',
      body: JSON.stringify({
        firmId: firm.id,
        taskIds: Array.from(selectedTaskIds),
        firstName,
        lastName,
        addressEmail,
        workHours,
        precedence: parseInt(precedence, 10),
      }),
      headers: {
        'Content-Type': 'application/json',
        'x-api-token': apiKey,
      },
    })
      .then(res => {
        this.setState({ saveSuccess: res.ok, submitting: false })
        return res.text()
      })
      .then(resBody => {
        if (!this.state.saveSuccess) {
          let errorMessage = resBody
          let errorEmail = '' as JSX.Element | string
          if (errorMessage.includes('no calendar visibility')) {
            errorEmail = (
              <span>
                {"I can't see this email account's calendar. "}
                <a
                  href="https://dynasty.helpdocs.io/app/content/article/kyqf1ds4cc"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Help me fix this.
                </a>
              </span>
            )
            errorMessage = ''
          }

          this.setState({ errorEmail, errorMessage, submitting: false })
        }
      })
      .catch(err => {
        console.log(err)
        this.setState({ errorMessage: err })
        window.scrollTo(0, 0)
      })
  }

  // TODO: move validation to front-end.
  private workHoursValid = (workHours: string) => workHours.length > 0

  private propertiesTablerowKeyFn = (row: Task) => row.id

  private propertiesTable = () => {
    const { selectedTaskIds } = this.state
    const { tasks } = this.props.firm

    const workHours = this.workHoursRef.current!.value.trim().toUpperCase()
    const selectedTasks = tasks.filter(task => selectedTaskIds.has(task.id))

    const colFns = [(row: Task) => row.displayName, () => workHours]

    return <Table keyFn={this.propertiesTablerowKeyFn} rows={selectedTasks} columns={colFns} />
  }

  private errorField = (errorMessage: JSX.Element | string) => (
    <span>
      {errorMessage && (
        <span>
          <Spacer height={4} />
          <BodyText color="coral">{errorMessage}</BodyText>
        </span>
      )}
    </span>
  )

  private newAgentForm = () => {
    const {
      precedence,
      errorTaskIds,
      errorFirstName,
      errorLastName,
      errorEmail,
      errorWorkHours,
      errorMessage,
      submitting,
    } = this.state

    const { tasks } = this.props.firm

    return (
      <FormWrapper>
        <Body>
          <Spacer height={24} />
          <H1>Where will this agent work?</H1>
          <Spacer height={32} />

          <Spacer height={16} />
          <TasksTableMultiselect tasks={tasks} onChange={this.onSelectedTaskIdsChange} />

          {this.errorField(errorTaskIds)}
          <Spacer height={32} />

          <H3>First name</H3>
          <InputLarge maxLength={255} placeholder="Jane" hasError={errorFirstName !== ''} ref={this.firstNameRef} />
          <Spacer height={4} />
          {this.errorField(errorFirstName)}
          <Spacer height={32} />

          <H3>Last name</H3>
          <InputLarge maxLength={255} placeholder="Doe" hasError={errorLastName !== ''} ref={this.lastNameRef} />
          <Spacer height={4} />
          {this.errorField(errorLastName)}
          <Spacer height={32} />

          <H3>Email address</H3>
          <InputLarge
            maxLength={255}
            placeholder="jane.doe@company.com"
            hasError={errorEmail !== ''}
            ref={this.addressEmailRef}
          />
          <Spacer height={4} />
          {this.errorField(errorEmail)}
          <Spacer height={32} />

          <H3>Role</H3>
          <ToggleWrapper>
            <Toggle options={this.roles} value={precedence} onSelect={this.toggleOnClick} />
          </ToggleWrapper>
          <Spacer height={32} />

          <Tooltip contentRenderer={getShowingHoursHelpMsg}>
            <H3>Showing hours</H3>
          </Tooltip>
          <InputLarge hasError={errorWorkHours !== ''} placeholder="M-W 9am-5pm Th 10am-4pm" ref={this.workHoursRef} />
          <Spacer height={4} />
          {this.errorField(errorWorkHours)}
          <Spacer height={32} />

          <ErrorWrapper>
            {errorMessage && (
              <span>
                <H3 color="coral">{errorMessage}</H3>
                <Spacer height={16} />
              </span>
            )}
          </ErrorWrapper>

          <Spacer height={48} />
          <ButtonWrapper>
            <Button onClick={this.addAgentButtonOnClick} disabled={submitting}>
              Add New Agent
            </Button>
          </ButtonWrapper>
          <Spacer height={48} />
          <br />
        </Body>
      </FormWrapper>
    )
  }

  private errorBody = () => (
    <Body>
      <Spacer height={64} />
      <H1>Sorry</H1>
      <IntroText>Something went wrong. Please click the link we sent you again.</IntroText>
      <Spacer height={64} />
    </Body>
  )

  render() {
    const { saveSuccess } = this.state
    const { firm } = this.props

    if (!firm) {
      return this.errorBody()
    }

    return saveSuccess ? this.successBody() : this.newAgentForm()
  }
}

/**
 *
 * Provides server-rendered HTML widget.
 */
export async function getAddAgentHTML(firmId: string) {
  const client = createRequestScopedClient()

  const res = await client.query<FetchFirmTasksQuery, FetchFirmTasksQueryVariables>({
    query: fetchFirmTasksQuery,
    variables: {
      firmId: parseInt(firmId, 10),
    },
  })

  if (res.data == null) {
    return res.error?.message || 'new agent information not available'
  }

  const props = { firm: res.data.firm }
  return addAgentWidget.renderHtml('New Agent Information', props, props)
}

/**
 * Hydrates server-rendered widget.
 */
export function hydrateAddAgentWidget(renderNotHydrate?: boolean) {
  // Only hydrate widget if it's actually rendered
  if (document.getElementById(addAgentWidgetId) == null) {
    return
  }

  addAgentWidget.init(appContext().hydrateProps, renderNotHydrate)
}

const ToggleWrapper = styled.span({
  lineHeight: '30px',
})

const Body = styled.div({
  boxSizing: 'border-box',
  maxWidth: '579px',
  margin: '0 auto',
  padding: '0 48px 64px',
})

const FormWrapper = styled.div({
  background: Colors.ink,
})

const ErrorWrapper = styled.span({
  width: '100%',
  display: 'flex',
  justifyContent: 'right',
})

const ButtonWrapper = styled.span({
  width: '100%',
  display: 'flex',
  justifyContent: 'right',
})

const H1 = styled.h1({
  color: 'white',
  fontFamily: Fonts.sansHeader,
  fontSize: '32px',
  fontWeight: 'normal',
  lineHeight: '1.5',
})

const H3 = styled.h3({
  color: 'white',
  fontFamily: Fonts.sansHeader,
  fontSize: '21px',
  fontWeight: 'normal',
  lineHeight: '27px',
})

const IntroText = styled.div({
  fontSize: '16px',
  fontFamily: Fonts.sansBody,
  lineHeight: 1.62,
  color: 'silver',
})

const BodyText = styled.div({
  fontSize: '14px',
  fontFamily: Fonts.sansBody,
  lineHeight: 1.5,
  color: Colors.coral,
  fontFeatureSettings: 'inherit',
})

const Spacer = (props: { width?: number; height?: number; children?: React.ReactNode }) => {
  const SpacerDiv = styled.div({
    display: props.width ? 'inline-block' : 'block',
    height: props.height ? props.height + 'px' : 'auto',
    width: props.width ? props.width + 'px' : 'auto',
  })

  return <SpacerDiv>{props.children}</SpacerDiv>
}

const fgName = 'sky'
const fg = Colors[fgName]
const fgRgb = ColorsRGB[fgName]
const fgLight = LightColors[fgName]
const fgDark = DarkColors[fgName]

const colBorder = fg
const col = fg
const colBg = 'transparent'

const Button = styled.button({
  border: `1px solid ${colBorder}`,
  borderRadius: '4px',
  color: col,
  background: colBg,
  font: `16px ${Fonts.sansHeader}`,
  padding: '11px 24px 9px 24px',
  $nest: {
    '&:hover': {
      borderColor: fgLight,
      color: fgLight,
      background: `rgba(${fgRgb.r}, ${fgRgb.g}, ${fgRgb.b}, 0.1)`,
    },
    '&:active': {
      color: fgDark,
      borderColor: fgDark,
      background: Colors.black,
      opacity: 1,
    },
    '&:disabled': {
      opacity: 0.4,
      cursor: 'default',
      borderColor: colBorder,
      color: col,
      background: colBg,
    },
  },
})

const getShowingHoursHelpMsg = () => (
  <ShowingHoursContent>
    <BodyTextSpan>Enter days with abbreviations: M Tu W Th F Sa Su</BodyTextSpan>
    <Spacer height={4} />
    <BodyTextSpan>Indicate ranges with a dash: M-W</BodyTextSpan>
    <Spacer height={4} />
    <BodyTextSpan>
      Hours can be entered in segments. No commas between days: M 9:30am-12pm 3pm-5pm Tu-Sa 9am-5pm
    </BodyTextSpan>
  </ShowingHoursContent>
)

const ShowingHoursContent = styled.div({
  margin: '8px 12px 8px 12px',
})
